import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../../../../styles/panel/user/manageShop/ShopTrustSeal.css";
import http, { API_BASE_URL } from "../../../../api/http";
import { BACK_ICON } from "./shared/EditShopShared";
import {
  getTrust,
  saveStoreTrust,
} from "../../../../api/product_management/trust";

/* ---- آیکن‌های خطی هماهنگ با بقیه‌ی صفحات پنل ---- */
const Icon = ({ children, className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const ICONS = {
  trust: (
    <>
      <path d="M12 2 4 5v6c0 5 3.4 8.7 8 11 4.6-2.3 8-6 8-11V5z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </>
  ),
  check: <polyline points="20 6 9 17 4 12" />,
};

const isValidLink = (value) => /^https?:\/\/.+/i.test(value.trim());

export default function ShopTrustSeal() {
  const { shopId } = useParams();
  const navigate = useNavigate();

  const [trustLink, setTrustLink] = useState("");
  const [initialLink, setInitialLink] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [imgStatus, setImgStatus] = useState("idle"); // idle | loading | ok | error

  useEffect(() => {
    loadTrustLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId]);

  const loadTrustLink = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getTrust(shopId);
      const link = res?.data?.link || "";
      setTrustLink(link);
      setInitialLink(link);
    } catch (err) {
      console.log(err);
      setError("دریافت اطلاعات نماد اعتماد با خطا مواجه شد.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!trustLink.trim()) {
      setError("لطفاً لینک نماد اعتماد رو وارد کن.");
      return;
    }
    if (!isValidLink(trustLink)) {
      setError("لینک وارد شده معتبر نیست؛ باید با http یا https شروع بشه.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      await saveStoreTrust({ link: trustLink.trim(), store_id: shopId });

      setInitialLink(trustLink.trim());
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.log(err);
      setError("ذخیره لینک با خطا مواجه شد. دوباره تلاش کن.");
    } finally {
      setSaving(false);
    }
  };

  const isDirty = trustLink.trim() !== initialLink.trim();

  return (
    <div className="trust-page">
      <div className="trust-head">
        <button
          type="button"
          className="esp-back-btn"
          onClick={() => navigate(-1)}
          aria-label="بازگشت"
        >
          <Icon>{BACK_ICON}</Icon>
        </button>
        <div>
          <h1>نماد اعتماد</h1>
          <p>لینک نماد اعتماد الکترونیکی فروشگاهت رو اینجا ثبت یا ویرایش کن.</p>
        </div>
      </div>

      {loading ? (
        <div className="trust-empty">
          <p>در حال دریافت اطلاعات...</p>
        </div>
      ) : (
        <div className="trust-card">
          <div className="trust-card-ic">
            <Icon>{ICONS.trust}</Icon>
          </div>

          <label className="trust-label" htmlFor="trust-link-input">
            لینک نماد اعتماد
          </label>
          <div className="trust-input-wrap">
            <Icon className="trust-input-ic">{ICONS.link}</Icon>
            <input
              id="trust-link-input"
              type="text"
              inputMode="url"
              dir="ltr"
              placeholder="https://trustseal.enamad.ir/..."
              value={trustLink}
              onChange={(e) => {
                setTrustLink(e.target.value);
                setError("");
                setImgStatus("idle");
              }}
            />
          </div>

          {isValidLink(trustLink) && (
            <div className="trust-image-box">
              {imgStatus === "error" ? (
                <div className="trust-image-fallback">
                  <Icon className="trust-image-fallback-ic">{ICONS.trust}</Icon>
                  <span>نمایش تصویر نماد ممکن نیست</span>
                </div>
              ) : (
                <>
                  {imgStatus !== "ok" && (
                    <div className="trust-image-loading">
                      <span>در حال بارگذاری تصویر...</span>
                    </div>
                  )}
                  <img
                    key={trustLink.trim()}
                    className={`trust-image${imgStatus === "ok" ? " is-visible" : ""}`}
                    src={trustLink.trim()}
                    alt="نماد اعتماد الکترونیکی"
                    onLoad={() => setImgStatus("ok")}
                    onError={() => setImgStatus("error")}
                  />
                </>
              )}
            </div>
          )}

          {error && <p className="trust-error">{error}</p>}
          {saved && (
            <p className="trust-success">
              <Icon className="trust-success-ic">{ICONS.check}</Icon>
              لینک با موفقیت ذخیره شد.
            </p>
          )}

          <div className="trust-actions">
            <button
              type="button"
              className="trust-save-btn"
              onClick={handleSave}
              disabled={saving || !isDirty}
            >
              {saving ? "در حال ذخیره..." : "ذخیره لینک"}
            </button>
          </div>

          {initialLink && (
            <div className="trust-preview">
              <span className="trust-preview-label">پیش‌نمایش لینک فعلی</span>
              <div className="trust-preview-row">
                <a
                  className="trust-preview-thumb"
                  href={initialLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src={initialLink}
                    alt="نماد اعتماد الکترونیکی"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </a>
                <a
                  className="trust-preview-link"
                  href={initialLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  {initialLink}
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
